import React, {
  useState, useEffect, useContext,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CarbonForm from '../CarbonForm';
import RequestModal from '../../../../../../components/RequestModal';
import Loader from '../../../../../../components/Loader';
import './CarbonFormDataProvider.scss';

import getConfig from '../../../../../../config';
import { OldDataStructure, newDataStructure } from '../../data-structure';
import { getJSONFileFromIpfs, initIPFS } from '../../../../../../state/ipfs';
import { sendRequest } from '../../../../../../utils/api-request';
import { appStore } from '../../../../../../state/app';

const { backendApi } = getConfig();

const CarbonFormDataProvider = ({ projectData, initialTabKey }) => {
  const translate = useIntl();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [carbonData, setCarbonData] = useState({});
  const { update } = useContext(appStore);
  const {
    project_id: projectId,
    carbonApplication,
    regionShortCode,
    square,
    avgTrees,
    leakage,
  } = projectData || {};
  const getPublicCarbonData = async () => {
    try {
      const cid = carbonApplication?.cid;
      if (!cid) return null;

      const ipfs = await initIPFS();
      const res = await getJSONFileFromIpfs(ipfs, cid);
      return res || null;
    } catch (e) {
      console.log(e);
      return null;
    }
  };

  const getPrivateCarbonData = async () => {
    const { result } = await sendRequest(`${backendApi}/api/dashboard/get-carbon-form?project_id=${projectId}`, 'GET', {}, true);
    return result || null;
  };

  const isFormDisabled = (publicData, privateData) => {
    if (leakage && !leakage.carbonApprove) return false;
    const flag1 = !!publicData?.completed;
    const flag2 = !!privateData?.completed;
    return flag1 && flag2; // true
  };

  const checkOldOrNewTypeOfProject = (data, privateData) => {
    if (carbonApplication?.completed) {
      const globData = Object.values(data);
      const isNewProject = globData.some((elem) => Object.keys(elem).includes('treeSpeciesPlanted'));
      if (isNewProject) {
        return 'newType';
      } if (!isFormDisabled(carbonApplication, privateData)) {
        return 'newType';
      }
      return 'oldType';
    }
    return 'newType';
  };

  const getCarbonFormInfo = async () => {
    const [publicData, privateData] = await Promise.all([
      getPublicCarbonData(),
      getPrivateCarbonData(),
    ]);
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    // If no public data exist, set initial values and autofill some fields
    // If no private data exist, set initial values
    const typeOfProjectStructure = checkOldOrNewTypeOfProject(publicData, privateData);
    const structure = typeOfProjectStructure === 'oldType' ? OldDataStructure : newDataStructure;
    const defaultValues = structuredClone({
      ...structure,
      ...({
        ...publicData,
        generalInfo: {
          ...publicData?.generalInfo,
          country: regionNames.of(regionShortCode.toUpperCase()),
          projectSize: square.toString(),
          treeDensity: avgTrees.toString(),
        },
      } || {
        generalInfo: {
          ...structure.generalInfo,
          country: regionNames.of(regionShortCode.toUpperCase()),
          projectSize: square.toString(),
          treeDensity: avgTrees.toString(),
        },
      }),
      contactInfo: { ...(privateData ? { ...privateData.fields, documentationComment: privateData?.documentationComment } : structure.contactInfo), isUpdate: '' },
    });
    // Check if "completed" flags are synchronized in two sources (must be)
    // They can be out of sync in case if api call fails after smart-contract call
    const disabled = isFormDisabled(carbonApplication, privateData);
    const checkNewFields = Object.values(defaultValues).some((elem) => Object.keys(elem).includes('treeSpeciesPlanted'));
    const reUpdateValues = typeOfProjectStructure === 'oldType' || checkNewFields ? defaultValues : { ...defaultValues,
      projectActivity: { ...defaultValues.projectActivity,
        treeSpeciesPlanted: '',
        nonNative: '',
        totalPlantedTree: '' },
      landInfo: {
        ...defaultValues.landInfo,
        beDisplaced: '',

      } };
    return { carbonFormInfo: reUpdateValues, disabled };
  };

  const handleGetCarbonFormInfo = async () => {
    const { carbonFormInfo, disabled } = await getCarbonFormInfo();
    setCarbonData(carbonFormInfo);
    update('app.onSubmitedCarbonForm', disabled);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      // Get form fields values
      await handleGetCarbonFormInfo();
      setIsLoading(false);
    })();
  }, []);

  if (isLoading) {
    return (
      <div className="carbon-data-provider__loader-wrapper">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {isError && (
        <RequestModal
          error
          handleClose={() => setIsError(false)}
          title={translate.formatMessage({ id: 'modal.title.oops' })}
          description={(
            <FormattedMessage
              id="modal.description.somethingGoesWrong"
              values={{
                link: (...chunks) => <a href="mailto:support@openforestprotocol.org" className="text-anchor">{chunks}</a>,
              }}
            />
)}
        />
      )}
      <CarbonForm
        carbonData={carbonData}
        projectData={projectData}
        setIsError={setIsError}
        initialTabKey={initialTabKey}
      />
    </>
  );
};

export default CarbonFormDataProvider;
